import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button (docs/design/MASTER.md section 6, docs/design/components.md).
 *
 * Variants
 *  - default / primary: the one primary action on a screen, and store CTAs ("View at BHG Labs").
 *  - outline: the secondary action. MASTER calls it "secondary (outline)".
 *  - ghost: low-emphasis actions in toolbars and menus.
 *  - destructive, link: admin and inline use.
 *  - secondary: a grey filled button, kept for the admin screens that toggle between it and outline.
 *    Public pages use outline for a second action.
 *
 * Sizes
 *  - default: 44px on phones (the touch rule), 40px from 640px (.btn-size-default in index.css).
 *  - sm: 36px, for desktop tables only. Not on phones.
 *  - lg: 44px everywhere, wider padding.
 *  - icon: a square, 44px on phones and 40px from 640px. Give it an aria-label.
 *
 * Hover changes colour only: no glow, no lift, no shimmer. The six "signature" variants (magnetic,
 * iridescent, shimmer, lift, glass, underline) and their CSS were removed on 2026-10-22 with 0 uses.
 */
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium ring-offset-background transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        outline: "border border-input bg-background text-foreground hover:bg-secondary",
        ghost: "text-foreground hover:bg-secondary",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link: "text-primary underline-offset-4 hover:underline",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      },
      size: {
        default: "btn-size-default px-4",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "btn-size-icon",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
